import React from "react";
import { useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import StatusBadge from "designV2/components/StatusBadge";
import SettingsList from "superAdminViews/setting/SettingsList";

const ADD = gql`
  mutation createAttributeGroupByAdmin($rootCategory: ID!, $name: String!) {
    createAttributeGroupByAdmin(input: { rootCategory: $rootCategory, name: $name }) {
      _id
      name
    }
  }
`;

const GET = gql`
  query getAttributeGroupsByAdmin($limit: Int, $skip: Int) {
    getAttributeGroupsByAdmin(pagination: { limit: $limit, skip: $skip }) {
      _id
      name
      attributes {
        _id
        name
      }
      rootCategory {
        _id
        title
      }
    }
  }
`;

const COUNT = gql`
  query getAttributeGroupsByAdminCount {
    getAttributeGroupsByAdminCount
  }
`;

const UPDATE = gql`
  mutation updateAttributeGroupByAdmin($_id: ID!, $rootCategory: ID!, $name: String!) {
    updateAttributeGroupByAdmin(_id: $_id, input: { rootCategory: $rootCategory, name: $name }) {
      _id
      name
      rootCategory {
        _id
        title
      }
    }
  }
`;

const DELETE = gql`
  mutation deleteAttributeGroupByAdmin($idSet: [ID!]!) {
    deleteAttributeGroupByAdmin(idSet: $idSet) {
      _id
    }
  }
`;

const GET_ROOTS = gql`
  query getCategories {
    getCategories(filters: { parent: null }) {
      _id
      title
    }
  }
`;

/**
 * AttributeGroups - the headings product options are grouped under.
 *
 * A group belongs to one shop type, so "Size" for a restaurant and
 * "Size" for a grocery are separate groups with separate options.
 */
const AttributeGroups = () => {
  const { data } = useQuery(GET_ROOTS);
  const roots = (data && data.getCategories) || [];
  const rootOptions = roots.map((root) => ({ value: root._id, label: root.title }));

  return (
    <SettingsList
      title="Attribute groups"
      description="The headings a shop's product options are grouped under."
      singular="group"
      list={{ query: GET, key: "getAttributeGroupsByAdmin" }}
      count={{ query: COUNT, key: "getAttributeGroupsByAdminCount" }}
      create={{
        mutation: ADD,
        variables: (values) => ({
          name: values.name.trim(),
          rootCategory: values.rootCategory,
        }),
      }}
      update={{
        mutation: UPDATE,
        variables: (values, row) => ({
          _id: row._id,
          name: values.name.trim(),
          rootCategory: values.rootCategory,
        }),
      }}
      remove={{ mutation: DELETE }}
      columns={[
        { key: "name", label: "Group", width: "2fr" },
        {
          key: "rootCategory",
          label: "Shop type",
          render: (row) => (row.rootCategory ? row.rootCategory.title : "—"),
        },
        {
          key: "attributes",
          label: "Options",
          render: (row) => (
            <StatusBadge
              status={row.attributes && row.attributes.length ? "info" : "neutral"}
              label={`${(row.attributes || []).length} options`}
            />
          ),
        },
      ]}
      fields={[
        { name: "name", label: "Group", type: "text", required: true },
        {
          name: "rootCategory",
          label: "Shop type",
          type: "select",
          required: true,
          options: rootOptions,
        },
      ]}
      toValues={(row) => ({
        name: row.name,
        rootCategory: row.rootCategory ? row.rootCategory._id : "",
      })}
    />
  );
};

export default AttributeGroups;
